
import { ApiError } from '@eneco/dc/client/ApiError';
import { createBasket, getBasket, patchBasket } from '@eneco/dc/services/ShoppingBasketPublicService';
import type {
  DC_Domain_Models_Products_ShoppingBasket_BasketOffer,
  RequestDataDC_Domain_Models_Products_ShoppingBasket_ShoppingBasketRequest,
  ResponseDataDC_Domain_Models_Products_ShoppingBasket_ShoppingBasketResponse,
} from '@monorepo-types/dc';
import { assign, enqueueActions, fromPromise, setup } from 'xstate';
import type { EventObject } from 'xstate';

import { FlowContext, MachineConfig } from '../types/machine';

export interface BasketMachineContext extends FlowContext {
  errorMessage?: string;
  basketId?: string;
  cachedOffers?: DC_Domain_Models_Products_ShoppingBasket_BasketOffer[];
}

export type BasketMachinesState = {
  value: 'IDLE' | 'CREATING' | 'FETCHING' | 'UPDATING' | 'CLOSING' | 'SUCCESS' | 'ERROR' | 'CLOSED';
  context: BasketMachineContext;
};

export interface BasketMachineEvent extends EventObject {
  type: 'GET_BASKET' | 'CREATE_BASKET' | 'UPDATE_BASKET' | 'CLOSE_BASKET' | 'CLOSED';
  config?: MachineConfig;
  id?: string;
  values?: Omit<FlowContext, 'machines'>;
}

const getValuesFormAttributes = (attributes?: { [key: string]: string } | null) => {
  if (attributes?.flowState) {
    return JSON.parse(attributes.flowState);
  }

  return {};
};

export const basketMachine = setup({
  types: {} as {
    context: BasketMachineContext;
    events: BasketMachineEvent;
    output: ResponseDataDC_Domain_Models_Products_ShoppingBasket_ShoppingBasketResponse;
  },
  actors: {
    createBasket: fromPromise(async ({ input: event }: { input: BasketMachineEvent }) => {
      try {
        if (!event?.config) throw new Error('No config provided');
        return await createBasket({
          businessUnit: event.config.businessUnit,
          label: event.config.label,
          requestBody: {},
        });
      } catch (error) {
        throw new Error((error as ApiError)?.message);
      }
    }),
    getBasket: fromPromise(async ({ input: event }: { input: BasketMachineEvent }) => {
      try {
        if (!event?.config || !event?.id) throw new Error('No config or id provided');
        return await getBasket({
          businessUnit: event.config.businessUnit,
          label: event.config.label,
          id: event.id,
        });
      } catch (error) {
        throw new Error(JSON.stringify({ error: (error as ApiError)?.message, config: event.config }));
      }
    }),
    updateBasket: fromPromise(async ({ input: event }: { input: BasketMachineEvent }) => {
      try {
        if (!event?.config || !event?.id) throw new Error('No config or id provided');
        const requestBody: RequestDataDC_Domain_Models_Products_ShoppingBasket_ShoppingBasketRequest = {
          data: {
            attributes: { flowState: JSON.stringify(event.values) },
          },
        };
        return await patchBasket({
          businessUnit: event.config.businessUnit,
          label: event.config.label,
          id: event.id,
          requestBody,
        });
      } catch (error) {
        throw new Error((error as ApiError)?.message);
      }
    }),
    closeBasket: fromPromise(async ({ input: event }: { input: BasketMachineEvent }) => {
      try {
        if (!event?.config || !event?.id) throw new Error('No config or id provided');
        const requestBody: RequestDataDC_Domain_Models_Products_ShoppingBasket_ShoppingBasketRequest = {
          data: {
            status: 'Ordered',
            attributes: { flowState: JSON.stringify(event.values) },
          },
        };
        return await patchBasket({
          businessUnit: event.config.businessUnit,
          label: event.config.label,
          id: event.id,
          requestBody,
        });
      } catch (error) {
        throw new Error((error as ApiError)?.message);
      }
    }),
  },
}).createMachine({
  id: 'basket',
  initial: 'IDLE',
  context: {
    errorMessage: undefined,
  },
  on: {
    CLOSED: { target: '.CLOSED' },
  },
  states: {
    IDLE: {
      on: {
        GET_BASKET: { target: 'FETCHING' },
        CREATE_BASKET: { target: 'CREATING' },
        UPDATE_BASKET: { target: 'UPDATING' },
        CLOSE_BASKET: { target: 'CLOSING' },
      },
    },
    CREATING: {
      invoke: {
        id: 'createBasket',
        src: 'createBasket',
        input: ({ event }) => event,
        onDone: {
          target: 'SUCCESS',
          actions: assign({
            basketId: ({ event }) =>
              (event.output as ResponseDataDC_Domain_Models_Products_ShoppingBasket_ShoppingBasketResponse)?.data?.id,
            errorMessage: undefined,
          }),
        },
        onError: {
          target: 'ERROR',
          actions: assign({
            errorMessage: ({ event }) => String(event.error),
          }),
        },
      },
    },
    FETCHING: {
      invoke: {
        id: 'getBasket',
        src: 'getBasket',
        input: ({ event }: { context: BasketMachineContext; event: BasketMachineEvent }) => event,
        onDone: {
          target: 'SUCCESS',
          actions: enqueueActions(({ event, context, enqueue }) => {
            enqueue.assign({
              basketId: ({ event }) => event.output?.data?.id,
              ...getValuesFormAttributes(event.output?.data?.attributes),
              cachedOffers: event.output?.data?.offers || [],
              errorMessage: undefined,
            });
            if (event.output?.data?.status === 'Ordered') {
              enqueue.raise({ type: 'CLOSED' });
            }
          }),
        },
        onError: {
          target: 'ERROR',
          actions: enqueueActions(({ event, context, enqueue }) => {
            enqueue.assign({
              errorMessage: ({ event }) => String(event.error),
            });
            try {
              const parsedError = JSON.parse(String(event.error));
              if (parsedError.config) {
                enqueue.raise({ type: 'CREATE_BASKET', config: parsedError.config });
              }
            } catch {
              // If parsing fails, do nothing
            }
          }),
        },
      },
    },
    UPDATING: {
      invoke: {
        id: 'updateBasket',
        src: 'updateBasket',
        input: ({ event }: { context: BasketMachineContext; event: BasketMachineEvent }) => event,
        onDone: {
          target: 'SUCCESS',
          actions: assign({
            errorMessage: undefined,
          }),
        },
        onError: {
          target: 'ERROR',
          actions: assign({
            errorMessage: ({ event }) => String(event.error),
          }),
        },
      },
    },
    CLOSING: {
      invoke: {
        id: 'closeBasket',
        src: 'closeBasket',
        input: ({ event }: { context: BasketMachineContext; event: BasketMachineEvent }) => event,
        onDone: {
          target: 'CLOSED',
          actions: assign({
            errorMessage: undefined,
          }),
        },
        onError: {
          target: 'CLOSED',
          actions: assign({
            errorMessage: ({ event }) => String(event.error),
          }),
        },
      },
    },
    SUCCESS: {
      on: {
        GET_BASKET: { target: 'FETCHING' },
        CREATE_BASKET: { target: 'CREATING' },
        UPDATE_BASKET: { target: 'UPDATING' },
        CLOSE_BASKET: { target: 'CLOSING' },
      },
    },
    ERROR: {
      on: {
        CREATE_BASKET: { target: 'CREATING' },
      },
    },
    CLOSED: {
      type: 'final',
    },
  },
});
